import { useRef, useEffect } from 'react'

const EDGE_THRESHOLD = 50 // pixels from edge to trigger scroll
const MIN_SCROLL_SPEED = 2 // pixels per frame
const MAX_SCROLL_SPEED = 10 // pixels per frame at edge
const SCROLLSPEED = 6 // overall scroll speed multiplier

interface UseAutoScrollArgs {
  gridRef: React.RefObject<HTMLDivElement | null>
  enabled: boolean
  onScroll: (deltaX: number, deltaY: number) => void
  onPositionUpdate: (clientX: number, clientY: number) => void
}

interface UseAutoScrollReturn {
  handleMouseMove: (event: MouseEvent | React.MouseEvent) => void
  stopAutoScroll: () => void
}

function lerp(min: number, max: number, t: number): number {
  return min + (max - min) * Math.min(1, Math.max(0, t))
}

export function useAutoScroll({
  gridRef,
  enabled,
  onScroll,
  onPositionUpdate
}: UseAutoScrollArgs): UseAutoScrollReturn {
  const rafIdRef = useRef<number | null>(null)
  const mousePositionRef = useRef({ x: 0, y: 0 })
  const onScrollRef = useRef(onScroll)
  const onPositionUpdateRef = useRef(onPositionUpdate)

  // Keep refs in sync with props
  useEffect(() => {
    onScrollRef.current = onScroll
    onPositionUpdateRef.current = onPositionUpdate
  }, [onScroll, onPositionUpdate])

  // Calculate scroll velocity based on mouse position relative to grid edges
  const calculateVelocity = () => {
    if (!gridRef.current) return { x: 0, y: 0 }

    const rect = gridRef.current.getBoundingClientRect()
    const { x, y } = mousePositionRef.current
    let velocityX = 0
    let velocityY = 0

    if (x >= rect.left && x < rect.left + EDGE_THRESHOLD) {
      const distance = rect.left + EDGE_THRESHOLD - x
      velocityX = -lerp(MIN_SCROLL_SPEED, MAX_SCROLL_SPEED, distance / EDGE_THRESHOLD)
    } else if (x > rect.right - EDGE_THRESHOLD && x <= rect.right) {
      const distance = x - (rect.right - EDGE_THRESHOLD)
      velocityX = lerp(MIN_SCROLL_SPEED, MAX_SCROLL_SPEED, distance / EDGE_THRESHOLD)
    }

    if (y >= rect.top && y < rect.top + EDGE_THRESHOLD) {
      const distance = rect.top + EDGE_THRESHOLD - y
      velocityY = -lerp(MIN_SCROLL_SPEED, MAX_SCROLL_SPEED, distance / EDGE_THRESHOLD)
    } else if (y > rect.bottom - EDGE_THRESHOLD && y <= rect.bottom) {
      const distance = y - (rect.bottom - EDGE_THRESHOLD)
      velocityY = lerp(MIN_SCROLL_SPEED, MAX_SCROLL_SPEED, distance / EDGE_THRESHOLD)
    }

    return { x: velocityX * SCROLLSPEED, y: velocityY * SCROLLSPEED }
  }

  // Animation loop for continuous scrolling - uses refs to avoid stale closures
  const scrollLoopRef = useRef<() => void>(() => {})

  useEffect(() => {
    scrollLoopRef.current = () => {
      const velocity = calculateVelocity()

      if (velocity.x !== 0 || velocity.y !== 0) {
        onScrollRef.current(velocity.x, velocity.y)
        onPositionUpdateRef.current(mousePositionRef.current.x, mousePositionRef.current.y)
        rafIdRef.current = requestAnimationFrame(scrollLoopRef.current)
      } else {
        rafIdRef.current = null
      }
    }
  })

  // Handle mouse move - update position and start scroll loop if needed
  const handleMouseMove = (event: MouseEvent | React.MouseEvent) => {
    mousePositionRef.current = { x: event.clientX, y: event.clientY }

    // Start the scroll loop if not already running and enabled
    if (rafIdRef.current === null) {
      rafIdRef.current = requestAnimationFrame(scrollLoopRef.current)
    }
  }

  // Stop auto-scroll and cancel any pending animation frame
  const stopAutoScroll = () => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => stopAutoScroll()
  }, [stopAutoScroll])

  // Stop when disabled
  useEffect(() => {
    if (!enabled) {
      stopAutoScroll()
    }
  }, [enabled, stopAutoScroll])

  return {
    handleMouseMove,
    stopAutoScroll
  }
}
